/**
 * <ActivitiesPage> — l'onglet « Activités » de l'espace parent, et celui de l'élève (page).
 *
 * L'ordre du legacy (`TodoListScreen.js`), à l'identique : le titre et le menu « Activités
 * archivées », puis « Faites » (repliable, cinq lignes, « Voir plus »), puis « À faire ». Un
 * appui long sur une ligne ouvre la sélection multiple et la barre « Archiver (N) ».
 *
 * Deux sections, deux cartes-listes à plat : aucune surface n'est en relief ici, la page n'est
 * qu'une liste (`CLAUDE.md`, « Une seule surface en relief par page »).
 *
 * Le bloc lui-même est `<ActivitiesSection>` : la page n'y ajoute que son gabarit, son squelette
 * et la barre « Archiver (N) », qui est son pied.
 *
 * L'élève a le même onglet (`voice="student"`), avec ce qu'il a aujourd'hui sur son accueil
 * (`TodoListScreen.js`, branche élève) : « Faites », « À faire » qu'il range en la glissant
 * (`onReorder`), « Générer une activité ». Un ordre que le serveur n'a pas pris se dit en bas de
 * l'écran (`reorderNotice`), sans boîte : la liste, elle, est déjà revenue à sa place. Il ne sélectionne pas, n'archive pas et ne voit pas les archives : il ne passe ni
 * `onStartSelection` ni `onOpenArchived`, et le squelette ne garde pas la place de
 * « Sélectionner ».
 *
 * Contrôlée : la sélection (mode, éléments choisis, envoi en cours) arrive en props et remonte en
 * callbacks — chaque état s'ouvre seul dans une story.
 *
 * Le contenu d'une activité s'ouvre par `onOpenActivity`, et le « + » par `onGenerate` : la fiche
 * et « Générer une activité » sont deux surfaces à part, que la page ne monte pas.
 */
import React from "react"

import { SelectionActionBar } from "../../molecules/SelectionActionBar"
import { Snackbar } from "../../molecules/Snackbar"
import { ActivitiesSection, type ActivitiesSectionProps } from "../../templates/ActivitiesSection"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { ActivitiesSkeleton } from "./ActivitiesSkeleton"

export type ActivitiesPageProps = ActivitiesSectionProps & {
    onArchiveSelected?: () => void
    /** L'archivage est parti : la barre tourne et ne répond plus. */
    archiving?: boolean

    /**
     * L'ordre que l'élève vient de poser n'a pas été enregistré : la phrase qui le dit, en bas de
     * l'écran, et qui se retire seule. Rien par défaut. Ne se monte qu'avec `onReorder`.
     */
    reorderNotice?: string | null
    onDismissReorderNotice?: () => void

    // ── État de page ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void
}

export function ActivitiesPage(props: ActivitiesPageProps) {
    const {
        onArchiveSelected,
        archiving = false,
        reorderNotice = null,
        onDismissReorderNotice,
        loading = false,
        error,
        onRetry,
        testID,
        ...section
    } = props
    const { selectionMode = false, selectedIds = [] } = section

    return (
        <PageTemplate
            testID={testID}
            refreshing={section.refreshing}
            onRefresh={section.onRefresh}
            loading={loading}
            skeleton={
                <ActivitiesSkeleton
                    selectable={section.onStartSelection != null}
                    titleActions={section.onRefresh != null || section.onOpenArchived != null}
                />
            }
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
            footer={
                selectionMode ? (
                    <SelectionActionBar
                        count={selectedIds.length}
                        actionLabel="Archiver"
                        iconName="archive"
                        color="danger"
                        onAction={onArchiveSelected}
                        loading={archiving}
                    />
                ) : undefined
            }
        >
            <ActivitiesSection {...section} />
            {section.onReorder != null && (
                <Snackbar
                    visible={reorderNotice != null}
                    message={reorderNotice ?? ""}
                    tone="danger"
                    onDismiss={onDismissReorderNotice ?? NOOP}
                />
            )}
        </PageTemplate>
    )
}

/** Stable d'un rendu à l'autre : une fonction neuve relancerait le minuteur de retrait du `<Snackbar>`. */
const NOOP = () => {}
